import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    Command,
} from "@/components/ui/command";
import { ArrowUpRight, Terminal, Zap, FileText, Layers, Sparkles, BookOpen, History, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { scrollToId } from "./primitives";

export default function CommandPalette({ open, onOpenChange }) {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => {
            document.querySelector("[cmdk-input]")?.focus();
        }, 50);
        return () => clearTimeout(t);
    }, [open]);

    const run = (fn) => {
        onOpenChange(false);
        fn();
    };

    const go = (path, hash) => {
        onOpenChange(false);
        const navigating = location.pathname !== path;
        if (navigating) navigate(path);
        if (hash) setTimeout(() => scrollToId(hash), navigating ? 450 : 0);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="command-palette"
                className="overflow-hidden border-slate-800 bg-[#0D1420]/95 p-0 backdrop-blur-xl sm:max-w-lg"
            >
                <DialogTitle className="sr-only">Command palette</DialogTitle>
                <Command className="bg-transparent">
                    <CommandInput
                        data-testid="command-palette-input"
                        placeholder="Search docs, endpoints, actions…"
                        className="font-mono text-sm"
                    />
                    <CommandList>
                        <CommandEmpty className="py-8 text-center font-mono text-xs text-slate-500">
                            No results. Try “pricing” or “retry”.
                        </CommandEmpty>
                        <CommandGroup heading="Actions" className="font-mono text-xs">
                            <CommandItem
                                data-testid="palette-action-start-building"
                                onSelect={() => window.location.assign("https://mdhapi.com/signup")}
                                className="gap-2"
                            >
                                <Zap className="h-3.5 w-3.5 text-cyan-400" /> Start Building
                                <ArrowUpRight className="ml-auto h-3 w-3 text-slate-600" />
                            </CommandItem>
                            <CommandItem
                                data-testid="palette-action-dashboard"
                                onSelect={() => go("/dashboard")}
                                className="gap-2"
                            >
                                <LayoutDashboard className="h-3.5 w-3.5 text-cyan-400" /> Dashboard
                            </CommandItem>
                            <CommandItem
                                data-testid="palette-action-ai-assistant"
                                onSelect={() => go("/docs", "#assistant")}
                                className="gap-2"
                            >
                                <Sparkles className="h-3.5 w-3.5 text-teal-400" /> AI Docs Assistant
                            </CommandItem>
                            <CommandItem
                                data-testid="palette-action-read-docs"
                                onSelect={() => go("/docs")}
                                className="gap-2"
                            >
                                <BookOpen className="h-3.5 w-3.5 text-slate-400" /> Read the Docs
                            </CommandItem>
                            <CommandItem
                                data-testid="palette-action-changelog"
                                onSelect={() => go("/changelog")}
                                className="gap-2"
                            >
                                <History className="h-3.5 w-3.5 text-slate-400" /> Changelog
                            </CommandItem>
                            <CommandItem
                                data-testid="palette-action-copy-curl"
                                onSelect={() =>
                                    run(async () => {
                                        try {
                                            await navigator.clipboard.writeText(
                                                'curl -X POST https://mdhapi.com/api/events -H "Authorization: Bearer $MDH_API_KEY" -d \'{ "type": "payment.succeeded", "data": { "id": "pay_4f92ka" } }\'',
                                            );
                                            toast.success("cURL quickstart copied");
                                        } catch {
                                            toast.error("Copy failed");
                                        }
                                    })
                                }
                                className="gap-2"
                            >
                                <Terminal className="h-3.5 w-3.5 text-teal-400" /> Copy cURL quickstart
                            </CommandItem>
                        </CommandGroup>
                        <CommandGroup heading="Navigate" className="font-mono text-xs">
                            {[
                                ["Capabilities", "#capabilities", Layers],
                                ["Command Center", "#command-center", Zap],
                                ["Developer Workflow", "#developers", Terminal],
                                ["FAQ", "#faq", FileText],
                            ].map(([label, href, Icon]) => (
                                <CommandItem
                                    key={href}
                                    data-testid={`palette-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                    onSelect={() => go("/", href)}
                                    className="gap-2"
                                >
                                    <Icon className="h-3.5 w-3.5 text-slate-500" /> {label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </DialogContent>
        </Dialog>
    );
}
